import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import FilterChip from '../../components/crm/FilterChip';
import EmptyState from '../../components/crm/EmptyState';
import AuditScoreChip from '../../components/crm/AuditScoreChip';
import PaginatedTable from '../../components/admin/PaginatedTable';
import usePaginatedQuery from '../../hooks/usePaginatedQuery';
import MissingPdfsBanner from '../../components/admin/MissingPdfsBanner';

const SCORE_BUCKETS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'pass', label: 'Pass ≥80', params: { min_score: 80 } },
  { key: 'warn', label: 'Warn 60-79', params: { min_score: 60, max_score: 79 } },
  { key: 'fail', label: 'Fail <60', params: { max_score: 59 } },
];

export default function Audits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const bucketKey = searchParams.get('bucket') || 'all';
  const bucket = SCORE_BUCKETS.find((b) => b.key === bucketKey) || SCORE_BUCKETS[0];

  const setBucket = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'all') next.delete('bucket');
      else next.set('bucket', key);
      next.delete('offset');
      return next;
    });
  };

  const paginator = usePaginatedQuery({
    path: '/api/admin/audits',
    responseKey: 'audits',
    extraParams: { ...bucket.params },
  });

  const anyFilterSet = bucketKey !== 'all';

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" />;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Audits</span></h1>
        <p className="text-[13px] text-gray-500 mt-0.5">Accessibility audit history across all accounts.</p>
      </div>
      <MissingPdfsBanner />
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {SCORE_BUCKETS.map((b) => (
          <FilterChip key={b.key} active={bucketKey === b.key} onClick={() => setBucket(b.key)}>{b.label}</FilterChip>
        ))}
      </div>
      <PaginatedTable
        paginator={paginator}
        anyFilterSet={anyFilterSet}
        emptyMessage="No audits yet."
        emptyFilteredMessage="No audits match"
        renderTable={(rows) => (
          <div className="space-y-2">
            {rows.map((a) => (
              <button key={a.id} onClick={() => navigate(`/admin/audits/${a.id}`)}
                className="w-full text-left p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] text-white font-mono truncate">{a.auditedUrl}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {a.producedAt} · {a.producedBy} ·
                    crit {a.violationsCritical ?? 0} / ser {a.violationsSerious ?? 0} / mod {a.violationsModerate ?? 0} / min {a.violationsMinor ?? 0}
                  </div>
                </div>
                <AuditScoreChip score={a.score} showLabel />
              </button>
            ))}
          </div>
        )}
      />
    </div>
  );
}
